import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { ReceiveSupplyOrder } from "../../../services/InventoryService";
import useInventoryLocationList from "../../common/useInventoryLocationList";
import CommonLoader from "../../common/CommonLoader";

/**
 * Posts received quantities against an order. Each line is received into a
 * location, which is what moves the stock onto the product - the order status
 * follows automatically (Received Partially / Received in Full).
 */
const ReceiveSupplyOrderDialog = ({ open, order, onClose, onSave }) => {
  const [lines, setLines] = useState([]);
  // ENB-584: Inventory Locations (Warehouse > Rack > Shelf) as full paths; saved locations no longer active are kept.
  const locationOptions = useInventoryLocationList(
    lines.map((line) => line.receiveLocationName)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (order) {
      setLines(
        (order.items || []).map((item) => ({
          itemId: item.id,
          productName: item.productName,
          variantName: item.variantName,
          quantityOrdered: item.quantityOrdered,
          quantityReceived: item.quantityReceived,
          outstanding: Math.max(
            (Number(item.quantityOrdered) || 0) -
              (Number(item.quantityReceived) || 0),
            0
          ),
          // Default to receiving everything still outstanding.
          quantityReceivedNow: Math.max(
            (Number(item.quantityOrdered) || 0) -
              (Number(item.quantityReceived) || 0),
            0
          ),
          receiveLocationName: item.receiveLocationName || "",
        }))
      );
    } else {
      setLines([]);
    }
  }, [order]);

  const handleLineChange = (index, field, value) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    setLines(updated);
  };

  const handleSave = async () => {
    const postable = lines.filter(
      (line) => Number(line.quantityReceivedNow) > 0
    );

    if (postable.length === 0) {
      NotificationManager.warning("Enter a quantity to receive");
      return;
    }

    const missingLocation = postable.find(
      (line) => !line.receiveLocationName?.trim()
    );

    if (missingLocation) {
      NotificationManager.warning(
        `Choose a location to receive ${missingLocation.productName} into`
      );
      return;
    }

    try {
      setLoading(true);
      const response = await ReceiveSupplyOrder({
        poNumber: order.poNumber,
        lines: postable.map((line) => ({
          itemId: line.itemId,
          quantityReceivedNow: Number(line.quantityReceivedNow),
          receiveLocationName: line.receiveLocationName,
        })),
      });
      setLoading(false);

      if (response.status === 200) {
        NotificationManager.success("Stock received successfully");
        onSave();
        onClose();
      } else {
        NotificationManager.error("Error receiving stock");
      }
    } catch (error) {
      setLoading(false);
      NotificationManager.error("Error receiving stock");
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Receive Stock {order ? `- PO ${order.poNumber}` : ""}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
            Quantities entered here are added to what has already been received
            and posted into the chosen location.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>Ordered</TableCell>
                <TableCell>Already Received</TableCell>
                <TableCell>Outstanding</TableCell>
                <TableCell>Receiving Now</TableCell>
                <TableCell>Into Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.itemId}>
                  <TableCell>{line.productName}</TableCell>
                  <TableCell>{line.variantName}</TableCell>
                  <TableCell>{line.quantityOrdered}</TableCell>
                  <TableCell>{line.quantityReceived}</TableCell>
                  <TableCell>{line.outstanding}</TableCell>
                  <TableCell>
                    <TextField
                      value={line.quantityReceivedNow}
                      onChange={(e) =>
                        handleLineChange(
                          index,
                          "quantityReceivedNow",
                          e.target.value
                        )
                      }
                      size="small"
                      variant="outlined"
                      type="number"
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      value={line.receiveLocationName}
                      onChange={(e) =>
                        handleLineChange(
                          index,
                          "receiveLocationName",
                          e.target.value
                        )
                      }
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 150 }}
                    >
                      {locationOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
              {lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="textSecondary">
                      This order has no lines to receive
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Receive
          </Button>
        </DialogActions>
      </Dialog>

      <CommonLoader loading={loading} />
    </>
  );
};

export default ReceiveSupplyOrderDialog;
